import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import { TextField } from '@verantos-dev/shared-components';

import type { ConversationSession } from '../types/gateway-chat-types';
import { formatConversationActivity } from '../utils/event-helpers';

interface GatewayChatSessionsPanelProps {
  conversations: ConversationSession[];
  activeConversationId: string;
  isLoadingConversations: boolean;
  isCreatingConversation: boolean;
  onRefreshConversations: () => void;
  onCreateConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  onRenameConversation: (conversationId: string, name: string) => Promise<void>;
}

export const GatewayChatSessionsPanel = ({
  conversations,
  activeConversationId,
  isLoadingConversations,
  isCreatingConversation,
  onRefreshConversations,
  onCreateConversation,
  onSelectConversation,
  onRenameConversation,
}: GatewayChatSessionsPanelProps) => {
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const startEditing = (conversation: ConversationSession) => {
    setEditingConversationId(conversation.conversation_id);
    setDraftName(conversation.conversation_name);
  };

  const cancelEditing = () => {
    setEditingConversationId(null);
    setDraftName('');
    setIsRenaming(false);
  };

  const submitRename = async () => {
    if (!editingConversationId) return;

    const trimmed = draftName.trim();
    if (!trimmed) {
      return;
    }

    try {
      setIsRenaming(true);
      await onRenameConversation(editingConversationId, trimmed);
      setEditingConversationId(null);
      setDraftName('');
    } catch (error) {
      console.error('Failed to rename chat', error);
    } finally {
      setIsRenaming(false);
    }
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingConversationId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingConversationId]);

  const filteredConversations = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return conversations;
    }
    return conversations.filter((conversation) =>
      conversation.conversation_name.toLowerCase().includes(trimmedQuery)
    );
  }, [searchQuery, conversations]);

  return (
    <Box
      sx={{
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Conversations
        </Typography>

        <IconButton aria-label="Refresh conversations" onClick={onRefreshConversations}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box px={2} pb={2}>
        <TextField
          id="chat-search"
          displayLabel=""
          placeholder="Search conversations"
          value={searchQuery}
          onChange={(_, value) => setSearchQuery(value)}
          size="small"
          width="auto"
          sx={{ mb: 1 }}
        />
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={onCreateConversation}
          disabled={isCreatingConversation}
        >
          New conversation
        </Button>
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {isLoadingConversations ? (
          <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              Loading conversations…
            </Typography>
          </Stack>
        ) : filteredConversations.length === 0 ? (
          <Stack
            height="100%"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            px={3}
            textAlign="center"
          >
            <Typography variant="body2" color="text.secondary">
              {searchQuery.trim()
                ? 'No conversations match your search.'
                : 'No conversations yet. Start one to see AG-UI streaming in action.'}
            </Typography>
          </Stack>
        ) : (
          <List disablePadding>
            {filteredConversations.map((conversation) => (
              <ListItemButton
                key={conversation.conversation_id}
                selected={conversation.conversation_id === activeConversationId}
                onClick={() => onSelectConversation(conversation.conversation_id)}
                sx={{ alignItems: 'flex-start', py: 1.5 }}
              >
                <Box sx={{ flex: 1, pr: 1 }}>
                  {editingConversationId === conversation.conversation_id ? (
                    <TextField
                      id={`conversation-name-${conversation.conversation_id}`}
                      displayLabel=""
                      value={draftName}
                      onChange={(_, value) => setDraftName(value)}
                      size="small"
                      width="auto"
                      inputRef={
                        editingConversationId === conversation.conversation_id
                          ? inputRef
                          : undefined
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void submitRename();
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          cancelEditing();
                        }
                      }}
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {conversation.conversation_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated{' '}
                        {formatConversationActivity(
                          conversation.last_activity ?? conversation.created_at
                        )}
                      </Typography>
                    </>
                  )}
                </Box>

                {editingConversationId === conversation.conversation_id ? (
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      aria-label="Save conversation name"
                      onClick={(event) => {
                        event.stopPropagation();
                        void submitRename();
                      }}
                      size="small"
                      disabled={isRenaming}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="Cancel editing"
                      onClick={(event) => {
                        event.stopPropagation();
                        cancelEditing();
                      }}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <IconButton
                    aria-label="Rename conversation"
                    onClick={(event) => {
                      event.stopPropagation();
                      startEditing(conversation);
                    }}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};
